/**
 * Registrant-facing fee line items for the checkout invoice (Part C).
 *
 * The API returns `fee_line_items` ALREADY FILTERED for the registrant: a fee
 * the league absorbs, or one an admin marked hidden, is simply not in the
 * array. The sibling fields on the session (`payment_processing_fee`,
 * `league_processing_fee_visible`, `payment_processing_fee_bearer`, …) exist
 * for admin surfaces.
 *
 * Never re-filter these rows client-side against those flags — that is how an
 * absorbed fee leaks onto a registrant's invoice.
 */
export type FeeLineItem = {
  key: string
  label: string
  amount: number
}

type SessionLike = {
  fee_line_items?: unknown
}

const isRenderableFee = (item: unknown): item is FeeLineItem => {
  if (!item || typeof item !== 'object') {
    return false
  }
  const { key, label, amount } = item as Partial<FeeLineItem>
  return (
    typeof key === 'string' &&
    key.length > 0 &&
    typeof label === 'string' &&
    label.length > 0 &&
    typeof amount === 'number' &&
    Number.isFinite(amount) &&
    // A zero-amount fee is noise on an invoice; a negative one is a discount
    // the API would express elsewhere. Neither belongs as a fee row.
    amount > 0
  )
}

/**
 * The fee rows to render between Subtotal and Tax. Returns [] for any payload
 * shape that isn't a usable array, so the invoice degrades to its previous
 * layout rather than throwing.
 */
export const getFeeLineItems = (session: SessionLike | null | undefined) => {
  const items = session?.fee_line_items
  if (!Array.isArray(items)) {
    return []
  }
  return items.filter(isRenderableFee)
}

/** Applied on the subtotal + taxed fees; rendered below the tax line. */
export const PAYMENT_PROCESSING_FEE_KEY = 'payment_processing_fee'

/**
 * Split registrant fee rows for receipt layout. League fees are taxed and sit
 * above Tax; payment processing is struck on the final amount and sits below.
 */
export const splitFeeLineItemsForReceipt = (items: FeeLineItem[]) => ({
  taxedFees: items.filter((item) => item.key !== PAYMENT_PROCESSING_FEE_KEY),
  postTaxFees: items.filter((item) => item.key === PAYMENT_PROCESSING_FEE_KEY),
})
